/**
 * Plan-gated features for Trish v2 hire onboarding (PRD: pricing before configure).
 * Intern must not tease locked Professional/Enterprise capabilities.
 */

import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import type { SchedulingLevel } from "~/lib/siteglue/consultant-onboarding";
import { SITEGLUE_CHANNEL_OPTIONS } from "~/lib/siteglue/consultant-onboarding";

const INTERN_CHANNELS = ["Website Chat", "Web Voice"] as const;

const ASSOCIATE_CHANNELS = [
  "Website Chat",
  "Phone Calls",
  "Web Voice",
  "Email",
] as const;

const PROFESSIONAL_CHANNELS = [...SITEGLUE_CHANNEL_OPTIONS] as string[];

export function channelsAllowedForPlan(planId: AiEmployeePlanId | null | undefined): string[] {
  const plan = planId ?? "intern";
  if (plan === "intern") return [...INTERN_CHANNELS];
  if (plan === "associate") return [...ASSOCIATE_CHANNELS];
  return [...PROFESSIONAL_CHANNELS];
}

/** Canonical deploy-channel labels for homepage / URL params (matches SITEGLUE_CHANNEL_OPTIONS). */
export const HERO_DEPLOY_CHANNEL_OPTIONS = [
  { id: "Website Chat", label: "Website Chat" },
  { id: "Web Voice", label: "Browser Voice" },
  { id: "Phone Calls", label: "Phone Calls" },
  { id: "SMS", label: "Text Messages" },
  { id: "Email", label: "Email" },
  { id: "WhatsApp", label: "WhatsApp" },
  { id: "Messenger", label: "Messenger" },
  { id: "Video Calls", label: "Video Calls" },
] as const;

export type HeroDeployChannelId = (typeof HERO_DEPLOY_CHANNEL_OPTIONS)[number]["id"];

/** Normalize free-form / URL channel labels to SITEGLUE_CHANNEL_OPTIONS names. */
export function normalizeDeployChannels(raw: string[]): string[] {
  const out: string[] = [];
  for (const item of raw) {
    const t = item.trim().toLowerCase();
    if (!t) continue;
    if (t === "all") {
      return [...SITEGLUE_CHANNEL_OPTIONS];
    }
    if (t === "ai_agents" || t === "ai-agents") {
      continue;
    }
    if (t === "live_chat" || t === "live-chat") {
      out.push("Website Chat");
      continue;
    }
    if (t === "phone_sms" || t === "phone-sms" || (t.includes("phone") && (t.includes("sms") || t.includes("text")))) {
      out.push("Phone Calls");
      out.push("SMS");
      continue;
    }
    if (
      t.includes("website") ||
      t.includes("live chat") ||
      (t.includes("chat") && !t.includes("whatsapp") && !t.includes("messenger"))
    ) {
      out.push("Website Chat");
    } else if (t.includes("webrtc") || (t.includes("voice") && !t.includes("video"))) {
      out.push("Web Voice");
    } else if (t.includes("phone")) {
      out.push("Phone Calls");
    } else if (t.includes("sms") || t.includes("text")) {
      out.push("SMS");
    } else if (t.includes("email")) {
      out.push("Email");
    } else if (t.includes("video")) {
      out.push("Video Calls");
    } else if (t.includes("whatsapp")) {
      out.push("WhatsApp");
    } else if (t.includes("messenger") || t.includes("facebook")) {
      out.push("Messenger");
    } else {
      const exact = SITEGLUE_CHANNEL_OPTIONS.find((c) => c.toLowerCase() === t);
      if (exact) out.push(exact);
    }
  }
  const unique = [...new Set(out)];
  return unique.length ? unique : ["Website Chat"];
}

export function parseChannelsFromParam(raw: string | null | undefined): string[] {
  if (!raw?.trim()) return [];
  return normalizeDeployChannels(
    raw
      .split(",")
      .map((s) => decodeURIComponent(s.trim()))
      .filter(Boolean),
  );
}

export function serializeChannelsParam(channels: string[]): string {
  return normalizeDeployChannels(channels).join(",");
}

/**
 * Lowest plan that includes every selected channel (Intern → Associate → Professional).
 * Used by homepage Build My AI Employee → starting plan from channel entitlements.
 */
export function minimumPlanForChannels(
  channels: string[],
): Exclude<AiEmployeePlanId, "enterprise"> {
  const normalized = normalizeDeployChannels(channels);
  const order: Exclude<AiEmployeePlanId, "enterprise">[] = [
    "intern",
    "associate",
    "professional",
  ];
  for (const plan of order) {
    const allowed = new Set(
      channelsAllowedForPlan(plan).map((c) => c.toLowerCase()),
    );
    if (normalized.every((c) => allowed.has(c.toLowerCase()))) {
      return plan;
    }
  }
  return "professional";
}

/** Max scheduling capability included in the plan (cap recommendations downward). */
export function schedulingLevelsAllowedForPlan(
  planId: AiEmployeePlanId | null | undefined,
): SchedulingLevel[] {
  const plan = planId ?? "intern";
  if (plan === "intern") {
    return ["none", "appointment_request"];
  }
  if (plan === "associate") {
    return ["none", "appointment_request", "basic"];
  }
  if (plan === "professional") {
    return ["none", "appointment_request", "basic", "advanced"];
  }
  return ["none", "appointment_request", "basic", "advanced", "enterprise"];
}

export function clampSchedulingLevelToPlan(
  level: SchedulingLevel,
  planId: AiEmployeePlanId | null | undefined,
): SchedulingLevel {
  const allowed = schedulingLevelsAllowedForPlan(planId);
  if (allowed.includes(level)) return level;
  return allowed[allowed.length - 1] ?? "appointment_request";
}

export function filterChannelsToPlan(
  channels: string[],
  planId: AiEmployeePlanId | null | undefined,
): string[] {
  const allowed = new Set(channelsAllowedForPlan(planId).map((c) => c.toLowerCase()));
  const filtered = channels.filter((c) => allowed.has(c.toLowerCase()));
  if (filtered.length) return filtered;
  return channelsAllowedForPlan(planId).slice(0, 1);
}

/** Intern-safe career-path line — prefer “promote” over “upgrade”. */
export function upgradeNudgeCopy(planId: AiEmployeePlanId | null | undefined): string {
  if (planId === "intern" || !planId) {
    return "As your needs grow, you can promote your AI Employee to Associate or Professional to add more communication capabilities and responsibilities.";
  }
  if (planId === "associate") {
    return "When you're ready, you can promote to Professional for broader workflows and integrations.";
  }
  return "";
}

export const EMPLOYEE_PERSONALITY_OPTIONS = [
  "Friendly",
  "Professional",
  "Warm",
  "Luxury",
  "Technical",
] as const;

export type EmployeePersonality = (typeof EMPLOYEE_PERSONALITY_OPTIONS)[number];

export const EMPLOYEE_PHOTO_CHOICES = [
  "Professional Female",
  "Professional Male",
  "Upload later",
] as const;

export type EmployeePhotoChoice = (typeof EMPLOYEE_PHOTO_CHOICES)[number];
